import { useEffect, useState } from "react";
import {
  createTestimonial,
  deleteTestimonial,
  getTestimonials,
  updateTestimonial,
} from "../../../api/apiServices";

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const emptyTestimonial = {
    quote: "",
    author: "",
    role: "Survivor",
    image: "",
    isPublished: true,
  };

  /* ---------------- LOAD ---------------- */
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const res = await getTestimonials();
      setTestimonials(res.data || []);
    } catch (err) {
      console.error("Failed to load testimonials", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- IMAGE HANDLER ---------------- */
  const handleImageChange = (file) => {
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  /* ---------------- SAVE ---------------- */
  const handleSave = async () => {
    try {
      const formData = new FormData();

      formData.append("author", editingTestimonial.author);
      formData.append("role", editingTestimonial.role);
      formData.append("quote", editingTestimonial.quote);
      formData.append("isPublished", editingTestimonial.isPublished);

      if (imageFile) {
        formData.append("avatar", imageFile);
      }

      if (isAdding) {
        await createTestimonial(formData);
      } else {
        await updateTestimonial(editingTestimonial._id, formData);
      }

      await fetchTestimonials();

      setEditingTestimonial(null);
      setIsAdding(false);
      setImageFile(null);
      setImagePreview("");

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this testimonial?")) return;

    try {
      await deleteTestimonial(id);
      setTestimonials(testimonials.filter((t) => t._id !== id));
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* ---------------- TOGGLE PUBLISH ---------------- */
  const togglePublish = async (testimonial) => {
    try {
      const formData = new FormData();
      formData.append("isPublished", !testimonial.isPublished);

      await updateTestimonial(testimonial._id, formData);

      setTestimonials((prev) =>
        prev.map((t) =>
          t._id === testimonial._id
            ? { ...t, isPublished: !t.isPublished }
            : t
        )
      );
    } catch (err) {
      console.error("Publish toggle failed", err);
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Testimonials
          </h2>
          <p className="text-gray-600 text-sm">
            Manage survivor testimonials
          </p>
        </div>

        <button
          onClick={() => {
            setEditingTestimonial(emptyTestimonial);
            setIsAdding(true);
            setImagePreview("");
            setImageFile(null);
          }}
          className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg flex items-center gap-2"
        >
          <i className="ri-add-line" />
          Add Testimonial
        </button>
      </div>

      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          Testimonial saved successfully
        </div>
      )}

      {/* GRID */}
      {!editingTestimonial && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            testimonials.map((testimonial) => (
              <div
                key={testimonial._id}
                className="bg-white rounded-xl p-6 border"
              >
                <div className="flex justify-between mb-4">
                  <img
                    src={testimonial.image}
                    className="w-16 h-16 rounded-full object-cover"
                    alt=""
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingTestimonial(testimonial);
                        setIsAdding(false);
                        setImagePreview(testimonial.image);
                        setImageFile(null);
                      }}
                    >
                      <i className="ri-edit-line" />
                    </button>
                    <button
                      onClick={() => handleDelete(testimonial._id)}
                    >
                      <i className="ri-delete-bin-line text-red-500" />
                    </button>
                  </div>
                </div>

                <p className="italic text-sm mb-4">
                  “{testimonial.quote}”
                </p>

                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-sm">
                      {testimonial.author}
                    </p>
                    <p className="text-xs text-[#6a4bc3]">
                      {testimonial.role}
                    </p>
                  </div>

                  <button
                    onClick={() => togglePublish(testimonial)}
                    className={`px-3 py-1 rounded-full text-xs ${
                      testimonial.isPublished
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {testimonial.isPublished ? "Published" : "Draft"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* FORM */}
      {editingTestimonial && (
        <div className="bg-white rounded-xl p-6 border space-y-4">
          <input
            className="input"
            placeholder="Author"
            value={editingTestimonial.author}
            onChange={(e) =>
              setEditingTestimonial({
                ...editingTestimonial,
                author: e.target.value,
              })
            }
          />

          <input
            className="input"
            placeholder="Role"
            value={editingTestimonial.role}
            onChange={(e) =>
              setEditingTestimonial({
                ...editingTestimonial,
                role: e.target.value,
              })
            }
          />

          {/* IMAGE */}
          <div className="space-y-2">
            {imagePreview && (
              <img
                src={imagePreview}
                className="w-32 h-32 rounded-full object-cover border"
                alt="Preview"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e.target.files[0])}
            />
          </div>

          <textarea
            rows={4}
            className="input"
            placeholder="Quote"
            value={editingTestimonial.quote}
            onChange={(e) =>
              setEditingTestimonial({
                ...editingTestimonial,
                quote: e.target.value,
              })
            }
          />

          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingTestimonial.isPublished}
              onChange={(e) =>
                setEditingTestimonial({
                  ...editingTestimonial,
                  isPublished: e.target.checked,
                })
              }
            />
            Publish testimonial
          </label>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSave}
              className="bg-[#6a4bc3] text-white px-6 py-2 rounded-lg"
            >
              {isAdding ? "Add Testimonial" : "Save Changes"}
            </button>

            <button
              onClick={() => {
                setEditingTestimonial(null);
                setIsAdding(false);
              }}
              className="border px-6 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
